import {
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";

import {
  areArraysEqual,
  ensureMinSelectedValues,
  getMinSelectedWarningText,
} from "~/components/UI/FilterDropdown/utils";
import type { IOption } from "~/types/option";

interface UseFilterMultiSelectDropdownParams<TValue> {
  options: IOption<TValue>[];
  value: TValue[];
  onChange: (value?: TValue[]) => void;
  minOptionCount?: number;
  exclude?: boolean;
}

export const useFilterDropdownMultiple = <TValue,>({
  options,
  value,
  onChange,
  minOptionCount = 1,
  exclude = false,
}: UseFilterMultiSelectDropdownParams<TValue>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const allOptionValues = useMemo(
    () => options.map((option) => option.value),
    [options]
  );

  const normalizedMinOptionCount = useMemo(() => {
    if (options.length === 0) {
      return 0;
    }

    return Math.min(Math.max(minOptionCount, 0), options.length);
  }, [minOptionCount, options.length]);

  const minimumSelectedValues = useMemo(() => {
    if (allOptionValues.length === 0) {
      return [];
    }

    return allOptionValues.slice(0, normalizedMinOptionCount);
  }, [allOptionValues, normalizedMinOptionCount]);

  const explicitSelectedValues = useMemo(() => {
    if (!value.length) {
      return [];
    }

    const valueSet = new Set(value);

    if (exclude) {
      return allOptionValues.filter((value) => !valueSet.has(value));
    }

    return allOptionValues.filter((value) => valueSet.has(value));
  }, [allOptionValues, exclude, value]);

  const normalizedExplicitSelectedValues = useMemo(
    () =>
      ensureMinSelectedValues({
        selectedValues: explicitSelectedValues,
        allOptionValues,
        minOptionCount: normalizedMinOptionCount,
      }),
    [allOptionValues, explicitSelectedValues, normalizedMinOptionCount]
  );

  const selectedValues = useMemo(() => {
    if (allOptionValues.length === 0) {
      return [];
    }

    if (!value.length) {
      return normalizedMinOptionCount > 0 ? allOptionValues : [];
    }

    if (normalizedExplicitSelectedValues.length === 0) {
      return minimumSelectedValues;
    }

    return normalizedExplicitSelectedValues;
  }, [
    allOptionValues,
    minimumSelectedValues,
    normalizedExplicitSelectedValues,
    normalizedMinOptionCount,
    value,
  ]);

  const selectedValueSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues]
  );

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedValueSet.has(option.value)),
    [options, selectedValueSet]
  );

  const chipTextValues = useMemo(() => {
    if (exclude) {
      return allOptionValues.filter((value) => !selectedValueSet.has(value));
    }

    return selectedValues;
  }, [allOptionValues, exclude, selectedValueSet, selectedValues]);

  const isAllSelected =
    allOptionValues.length > 0 &&
    selectedValues.length === allOptionValues.length;

  const visibleSelectedOptions = useMemo(
    () => selectedOptions.slice(0, 2),
    [selectedOptions]
  );

  const hiddenSelectedOptionsCount = Math.max(
    selectedOptions.length - visibleSelectedOptions.length,
    0
  );

  const persistSelectedValues = useCallback(
    (nextValues: TValue[]) => {
      const nextValueSet = new Set(nextValues);

      const normalizedValues = allOptionValues.filter((value) =>
        nextValueSet.has(value)
      );

      if (exclude) {
        const excludedValues = allOptionValues.filter(
          (value) => !nextValueSet.has(value)
        );

        onChange(excludedValues.length > 0 ? excludedValues : undefined);
        return;
      }

      if (normalizedValues.length === 0) {
        onChange(
          minimumSelectedValues.length > 0 ? minimumSelectedValues : undefined
        );
        return;
      }

      if (
        normalizedMinOptionCount > 0 &&
        normalizedValues.length === allOptionValues.length
      ) {
        onChange(undefined);
        return;
      }

      onChange(normalizedValues);
    },
    [
      allOptionValues,
      exclude,
      minimumSelectedValues,
      normalizedMinOptionCount,
      onChange,
    ]
  );

  useEffect(() => {
    if (!value?.length || allOptionValues.length === 0) {
      return;
    }

    if (
      !areArraysEqual(normalizedExplicitSelectedValues, explicitSelectedValues)
    ) {
      persistSelectedValues(normalizedExplicitSelectedValues);
    }
  }, [
    allOptionValues.length,
    explicitSelectedValues,
    normalizedExplicitSelectedValues,
    persistSelectedValues,
    value,
  ]);

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleToggleOption = useCallback(
    (value: TValue) => {
      const isSelected = selectedValueSet.has(value);

      if (isSelected) {
        if (selectedValues.length <= normalizedMinOptionCount) {
          toast.info(getMinSelectedWarningText(normalizedMinOptionCount));
          return;
        }

        persistSelectedValues(
          selectedValues.filter((selectedValue) => selectedValue !== value)
        );
        return;
      }

      persistSelectedValues([...selectedValues, value]);
    },
    [
      normalizedMinOptionCount,
      persistSelectedValues,
      selectedValueSet,
      selectedValues,
    ]
  );

  const handleToggleAll = useCallback(() => {
    if (allOptionValues.length === 0) {
      return;
    }

    if (isAllSelected) {
      persistSelectedValues(minimumSelectedValues);
      return;
    }

    persistSelectedValues(allOptionValues);
  }, [
    allOptionValues,
    isAllSelected,
    minimumSelectedValues,
    persistSelectedValues,
  ]);

  const handleDeleteChip = useCallback(
    (event: MouseEvent<HTMLElement>, value: TValue) => {
      event.stopPropagation();

      if (selectedValues.length === 1 && selectedValueSet.has(value)) {
        persistSelectedValues(allOptionValues);
        return;
      }

      handleToggleOption(value);
    },
    [
      handleToggleOption,
      allOptionValues,
      persistSelectedValues,
      selectedValueSet,
      selectedValues,
    ]
  );

  return {
    anchorEl,
    open,
    isAllSelected,
    selectedValueSet,
    chipTextValues,
    visibleSelectedOptions,
    hiddenSelectedOptionsCount,
    handleOpen,
    handleClose,
    handleToggleAll,
    handleToggleOption,
    handleDeleteChip,
  };
};
